import { useLayoutEffect, useRef, useState } from "react";

/**
 * Pin the projected-retreat action bar while scrolling.
 *
 * SingLayout uses overflow-x:hidden on ancestors, which traps `position:fixed`
 * so the bar must be portaled to document.body when pinned (see ActionBar).
 * Scroll is listened in capture phase so admin layout / window / nested
 * scrollers all trigger updates (guest public page only scrolls window).
 */
export function useProjectedActionBarPin(enabled: boolean) {
  const pageRootRef = useRef<HTMLDivElement>(null);
  const actionBarSentinelRef = useRef<HTMLDivElement>(null);
  const actionBarRef = useRef<HTMLDivElement>(null);
  const actionBarPinnedRef = useRef(false);
  const [actionBarPinned, setActionBarPinned] = useState(false);
  const [actionBarPin, setActionBarPin] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 56,
  });

  useLayoutEffect(() => {
    if (!enabled) return;

    let raf = 0;

    const syncPinnedBar = () => {
      const sentinel = actionBarSentinelRef.current;
      const root = pageRootRef.current;
      const bar = actionBarRef.current;
      if (!sentinel || !root) return;

      const header = document.querySelector(
        ".sing-header",
      ) as HTMLElement | null;
      const headerBottom = header
        ? Math.max(0, Math.round(header.getBoundingClientRect().bottom))
        : 8;
      const shouldPin = sentinel.getBoundingClientRect().top <= headerBottom + 1;
      const rootRect = root.getBoundingClientRect();
      const measuredHeight =
        bar?.offsetHeight || actionBarPin.height || 56;
      const next = {
        top: headerBottom + 8,
        left: Math.round(rootRect.left),
        width: Math.round(rootRect.width),
        height: measuredHeight,
      };

      if (actionBarPinnedRef.current !== shouldPin) {
        actionBarPinnedRef.current = shouldPin;
        setActionBarPinned(shouldPin);
      }

      setActionBarPin((prev) =>
        prev.top === next.top &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.height === next.height
          ? prev
          : next,
      );
    };

    const onScrollOrResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        syncPinnedBar();
      });
    };

    syncPinnedBar();
    // Capture phase catches scroll on window and nested layout containers.
    document.addEventListener("scroll", onScrollOrResize, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      document.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
    // actionBarPin.height intentionally omitted — read via closure for fallback only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return {
    pageRootRef,
    actionBarSentinelRef,
    actionBarRef,
    actionBarPinned,
    actionBarPin,
  };
}
